import {
  REPORT_CURRENCY_TYPE,
  REPORT_DATE_TYPE,
  REPORT_DATETIME_TYPE,
  REPORT_DECIMAL_TYPE,
  REPORT_INTEGER_TYPE,
} from 'Components/UI/EAReport/constants'
import { EAReportColumn } from 'Components/UI/EAReport/Table/utils/appendColumnPropertiesByType'
import { formatCurrency, formatDecimal, formatInteger, parseNumericValue } from 'Components/UI/EAReport/utils/format'
import moment from 'moment'

type AdjustFn<T extends Record<string, unknown>> = (record: T) => T

const adjustCurrency =
  <T extends Record<string, unknown>>(dataIndex: string): AdjustFn<T> =>
  (record) => {
    const value = parseNumericValue(record[dataIndex])
    return {
      ...record,
      [`${dataIndex}_csv`]: String(value).replace('.', ','),
      [`${dataIndex}_num`]: value,
      [`${dataIndex}_str`]: formatCurrency(value),
    }
  }

const adjustDate =
  <T extends Record<string, unknown>>(dataIndex: string): AdjustFn<T> =>
  (record) => {
    const aMomentDate = moment(record[dataIndex] as string)
    const isValidDate = aMomentDate.isValid()
    return {
      ...record,
      [`${dataIndex}_str`]: isValidDate ? aMomentDate.format('DD/MM/yyyy') : '-',
      [`${dataIndex}_unix`]: isValidDate ? aMomentDate.unix() : 0,
    }
  }

const adjustDateTime =
  <T extends Record<string, unknown>>(dataIndex: string): AdjustFn<T> =>
  (record) => {
    const aMomentDate = moment(record[dataIndex] as string)
    const isValidDate = aMomentDate.isValid()
    return {
      ...record,
      [`${dataIndex}_str`]: isValidDate ? aMomentDate.format('DD/MM/YYYY HH:mm:ss') : '-',
      [`${dataIndex}_unix`]: isValidDate ? aMomentDate.unix() : 0,
    }
  }

const adjustDecimal =
  <T extends Record<string, unknown>>(dataIndex: string): AdjustFn<T> =>
  (record) => {
    const value = parseNumericValue(record[dataIndex])
    return {
      ...record,
      [`${dataIndex}_csv`]: String(value).replace('.', ','),
      [`${dataIndex}_num`]: value,
      [`${dataIndex}_str`]: formatDecimal(value),
    }
  }

const adjustInteger =
  <T extends Record<string, unknown>>(dataIndex: string): AdjustFn<T> =>
  (record) => {
    const value = parseNumericValue(record[dataIndex])
    return {
      ...record,
      [`${dataIndex}_csv`]: String(value).replace('.', ','),
      [`${dataIndex}_num`]: value,
      [`${dataIndex}_str`]: formatInteger(value),
    }
  }

const adjustReportData = <T extends Record<string, unknown>>(columns: EAReportColumn<T>[], dataSource: T[]): T[] => {
  let dataSourceCopy = [...dataSource]

  columns.forEach((column) => {
    const dataIndex = column.dataIndex as string
    const { type } = column

    if (type === REPORT_CURRENCY_TYPE) dataSourceCopy = dataSourceCopy.map(adjustCurrency(dataIndex))
    if (type === REPORT_DATE_TYPE) dataSourceCopy = dataSourceCopy.map(adjustDate(dataIndex))
    if (type === REPORT_DATETIME_TYPE) dataSourceCopy = dataSourceCopy.map(adjustDateTime(dataIndex))
    if (type === REPORT_DECIMAL_TYPE) dataSourceCopy = dataSourceCopy.map(adjustDecimal(dataIndex))
    if (type === REPORT_INTEGER_TYPE) dataSourceCopy = dataSourceCopy.map(adjustInteger(dataIndex))
  })

  return dataSourceCopy
}

export default adjustReportData
